import { useLayoutEffect, useState } from 'react';

/**
 * Keeps returning `true` for `delayMs` after `value` flips to `false`, so a
 * consumer can hold its layout space (freezing its own animation, say) while
 * a sibling close animation finishes, instead of unmounting instantly and
 * shifting surrounding content.
 *
 * Uses useLayoutEffect (not useEffect) so the `value: false -> true` mirror
 * resolves before paint — otherwise the first render after re-opening would
 * briefly show the stale `false` from a previous close, popping the consumer
 * in a frame late and causing the exact class of layout jump this hook
 * exists to prevent, just on the opposite edge.
 */
export function useDelayedUnmount(value: boolean, delayMs: number): boolean {
	const [shouldRender, setShouldRender] = useState(value);

	useLayoutEffect(() => {
		if (value) {
			setShouldRender(true);
			return;
		}
		const timer = setTimeout(() => setShouldRender(false), delayMs);
		return () => clearTimeout(timer);
	}, [value, delayMs]);

	return shouldRender;
}
